import { useState } from "react"
import { useDispatch } from "react-redux"
import { Card, CardBody, CardFooter, CardTitle, Col, Row } from "reactstrap"
import { MdClose } from "react-icons/md"
import { ShareApplication } from "redux/slice/applications/applicationSlice"
import Notify from "components/Common/notify"

const EMAIL_REGEX =
  /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(?:\.[a-zA-Z]{2,})?$/

const dateStr = daysFromNow => {
  const d = new Date()
  d.setDate(d.getDate() + daysFromNow)
  return d.toISOString().split("T")[0]
}

export default function ApplicationShare({ applicationId, handleClose }) {
  const dispatch = useDispatch()
  const [emails, setEmails] = useState([])
  const [input, setInput] = useState("")
  const [expiryDate, setExpiryDate] = useState(dateStr(7))
  const [loading, setLoading] = useState(false)

  const addEmail = () => {
    const value = input.trim().toLowerCase()
    if (!value) return
    if (!EMAIL_REGEX.test(value)) {
      Notify("error", "Please enter a valid email address.")
      return
    }
    if (emails.includes(value)) {
      Notify("error", "This email is already added.")
      return
    }
    setEmails(prev => [...prev, value])
    setInput("")
  }

  const removeEmail = value => setEmails(prev => prev.filter(e => e !== value))

  const handleKeyDown = e => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault()
      addEmail()
    }
  }

  const handleSubmit = async () => {
    // include a typed-but-not-added email if it's valid
    let list = emails
    const pending = input.trim().toLowerCase()
    if (pending && EMAIL_REGEX.test(pending) && !emails.includes(pending)) {
      list = [...emails, pending]
      setEmails(list)
      setInput("")
    }
    if (!list.length) {
      Notify("error", "Add at least one email address.")
      return
    }
    if (!expiryDate) {
      Notify("error", "Please choose an expiry date.")
      return
    }
    setLoading(true)
    const res = await dispatch(
      ShareApplication({
        applicationId,
        emails: list,
        // send end-of-day in UTC so the chosen date stays valid all day
        expiryDate: new Date(`${expiryDate}T23:59:59`).toISOString(),
      }),
    )
    setLoading(false)
    if (res?.payload?.status === 200 || res?.payload?.status === 201) {
      handleClose()
    }
  }

  return (
    <Card className="bg-white">
      <CardBody className="mt-2">
        <CardTitle tag="h3" className="text-secondary-si fs-3">
          Share Application
        </CardTitle>
        <p className="text-muted">
          Recipients receive a secure, read-only link with an 8-digit access
          code.
        </p>

        <Row>
          <Col className="mt-3">
            <label>Recipient Emails</label>
            <div className="d-flex">
              <input
                type="email"
                className="form-control text-black me-2"
                placeholder="name@example.com"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <button
                type="button"
                className="btn btn-primary"
                style={{ whiteSpace: "nowrap" }}
                onClick={addEmail}
              >
                Add
              </button>
            </div>
            <small className="text-muted">
              Press Enter or comma to add each address.
            </small>

            {emails.length > 0 && (
              <div className="d-flex flex-wrap mt-2" style={{ gap: "8px" }}>
                {emails.map(email => (
                  <span
                    key={email}
                    className="badge d-inline-flex align-items-center"
                    style={{
                      background: "var(--theme-color-secondary)",
                      color: "#fff",
                      fontSize: "13px",
                      padding: "6px 10px",
                      borderRadius: "16px",
                      fontWeight: "normal",
                    }}
                  >
                    {email}
                    <MdClose
                      size={16}
                      style={{ cursor: "pointer", marginLeft: "6px" }}
                      onClick={() => removeEmail(email)}
                    />
                  </span>
                ))}
              </div>
            )}
          </Col>
        </Row>

        <Row>
          <Col className="mt-3">
            <label>Access Expires On</label>
            <input
              type="date"
              className="form-control text-black"
              value={expiryDate}
              min={dateStr(0)}
              onChange={e => setExpiryDate(e.target.value)}
            />
            <small className="text-muted">
              The link and code stop working after this date.
            </small>
          </Col>
        </Row>
      </CardBody>

      <CardFooter>
        <div className="mt-4 float-end">
          <button
            className="btn btn-primary me-2"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "Sharing…" : "Share"}
          </button>
          <button className="btn btn-danger" onClick={handleClose}>
            Close
          </button>
        </div>
      </CardFooter>
    </Card>
  )
}
